import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Tooltip,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Alert,
  Button,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  NotificationsActive as NotificationsActiveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';
import NotificationCenter from './NotificationCenter';

interface NotificationBellProps {
  showQuickPreview?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const NotificationBell: React.FC<NotificationBellProps> = ({ 
  showQuickPreview = true,
  size = 'medium'
}) => {
  const {
    unreadCount,
    permissionStatus,
    requestPermission,
    refreshNotifications,
    loading,
    notifications
  } = useNotifications();

  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const [quickMenuAnchor, setQuickMenuAnchor] = useState<null | HTMLElement>(null);

  const handleBellClick = (event: React.MouseEvent<HTMLElement>) => {
    if (showQuickPreview && notifications.length > 0) {
      setQuickMenuAnchor(event.currentTarget);
    } else {
      setNotificationCenterOpen(true);
    }
  };

  const handleQuickMenuClose = () => {
    setQuickMenuAnchor(null);
  };

  const handleOpenNotificationCenter = () => {
    setQuickMenuAnchor(null);
    setNotificationCenterOpen(true);
  };

  const handleRequestPermission = async () => {
    const granted = await requestPermission();
    if (granted) {
      console.log('✅ Notification permission granted');
    }
  };

  // Show different icons based on state
  const getNotificationIcon = () => {
    if (unreadCount > 0) {
      return <NotificationsActiveIcon />;
    } else if (notifications.length > 0) {
      return <NotificationsIcon />;
    } else {
      return <NotificationsNoneIcon />;
    }
  };

  // Get recent unread notifications for quick preview
  const recentUnreadNotifications = notifications
    .filter(n => !n.read)
    .slice(0, 3);

  return (
    <>
      {/* Permission Alert */}
      {permissionStatus === 'denied' && (
        <Alert 
          severity="warning" 
          action={
            <Button size="small" onClick={handleRequestPermission}>
              Enable
            </Button>
          }
          sx={{ mb: 1 }}
        >
          Enable notifications to stay updated
        </Alert>
      )}

      {/* Notification Bell */}
      <Tooltip 
        title={
          unreadCount > 0 
            ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
            : 'Notifications'
        }
      >
        <IconButton
          onClick={handleBellClick}
          size={size}
          color="inherit"
          sx={{
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'scale(1.1)',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
          disabled={loading}
        >
          <Badge 
            badgeContent={unreadCount > 99 ? '99+' : unreadCount}
            color="error"
            variant={unreadCount > 0 ? 'standard' : 'dot'}
            invisible={unreadCount === 0}
            sx={{
              '& .MuiBadge-badge': {
                animation: unreadCount > 0 ? 'pulse 1.5s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.1)' },
                  '100%': { transform: 'scale(1)' },
                },
              },
            }}
          >
            {getNotificationIcon()}
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Quick Preview Menu */}
      {showQuickPreview && (
        <Menu
          anchorEl={quickMenuAnchor}
          open={Boolean(quickMenuAnchor)}
          onClose={handleQuickMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 320,
              maxWidth: 400,
              maxHeight: 400,
            },
          }}
        >
          {/* Header */}
          <Box sx={{ p: 2, pb: 1 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" component="div">
                Notifications
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Badge badgeContent={unreadCount} color="error" variant="dot">
                  <NotificationsIcon fontSize="small" />
                </Badge>
                <IconButton 
                  size="small" 
                  onClick={refreshNotifications}
                  disabled={loading}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            {unreadCount > 0 && (
              <Typography variant="body2" color="textSecondary">
                {unreadCount} unread notification{unreadCount === 1 ? '' : 's'}
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Recent Notifications */}
          {recentUnreadNotifications.length > 0 ? (
            <>
              {recentUnreadNotifications.map((notification, _index) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => {
                    handleQuickMenuClose();
                    setNotificationCenterOpen(true);
                  }}
                  sx={{
                    whiteSpace: 'normal',
                    maxWidth: '100%',
                    borderLeft: '3px solid',
                    borderColor: `primary.main`,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography 
                      variant="subtitle2" 
                      fontWeight="bold"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        mb: 0.5,
                      }}
                    >
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(notification.created_at).toLocaleTimeString()}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
              
              <Divider />
              
              <MenuItem onClick={handleOpenNotificationCenter}>
                <Box textAlign="center" width="100%">
                  <Typography variant="body2" color="primary" fontWeight="medium">
                    View All Notifications
                  </Typography>
                </Box>
              </MenuItem>
            </>
          ) : notifications.length > 0 ? (
            <>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  All caught up! 🎉
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  No unread notifications
                </Typography>
              </Box>
              
              <Divider />
              
              <MenuItem onClick={handleOpenNotificationCenter}>
                <Box textAlign="center" width="100%">
                  <Typography variant="body2" color="primary" fontWeight="medium">
                    View All Notifications
                  </Typography>
                </Box>
              </MenuItem>
            </>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsNoneIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
              <Typography variant="body2" color="textSecondary">
                No notifications yet
              </Typography>
              <Typography variant="caption" color="textSecondary">
                You'll see your notifications here
              </Typography>
            </Box>
          )}

          {/* Permission Request */}
          {permissionStatus !== 'granted' && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Alert 
                  severity="info" 
                  action={
                    <Button size="small" onClick={handleRequestPermission}>
                      Enable
                    </Button>
                  }
                >
                  <Typography variant="caption">
                    Enable push notifications for real-time updates
                  </Typography>
                </Alert>
              </Box>
            </>
          )}
        </Menu>
      )}

      {/* Full Notification Center Dialog */}
      <NotificationCenter
        open={notificationCenterOpen}
        onClose={() => setNotificationCenterOpen(false)}
      />
    </>
  );
};

export default NotificationBell; 